/**
 * InvestorInterestModal — minimal pre-NDA view for an approved live deal.
 *
 * Shows ONLY (no address, no property specs, no derived ROI/profit):
 *   • Step 2  — Financials (Asking, Rehab, ARV, Closing Cost %, Wholesale Fee)
 *   • Step 3  — Type (property_type, deal_type)
 *   • Step 4  — Scope (repair_notes, additional_notes)
 *   • Step 5  — Media (photos + videos)
 *   • Agreement & Consent (vacancy status + occupancy description)
 *   • Interest CTA + optional note
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { fetchViewUrl } from "../lib/s3Upload";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";
import {
  DollarSign, Home, FileText, Camera, Film, Heart, Shield,
  ExternalLink, AlertCircle, CheckCircle2,
} from "lucide-react";

const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatCurrency = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US")}`;

const labelize = (k) =>
  k.split("_").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ");

const Stat = ({ label, value }) => (
  <div className="bg-muted/40 rounded-md p-3">
    <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
    <p className="text-sm font-semibold">{value}</p>
  </div>
);

const SectionHeader = ({ icon: Icon, children }) => (
  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
    <Icon className="w-3.5 h-3.5" />
    {children}
  </h3>
);

const InvestorInterestModal = ({ open, onOpenChange, deal, onInterestChange }) => {
  const [mediaUrls, setMediaUrls] = useState({ photos: {}, videos: {} });
  const [isInterested, setIsInterested] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingInterest, setLoadingInterest] = useState(false);

  // Hydrate signed view URLs for photos + videos
  useEffect(() => {
    if (!deal || !open) return;
    let cancelled = false;
    const hydrate = async (mediaMap) => {
      const urls = {};
      await Promise.all(
        Object.entries(mediaMap || {}).map(async ([key, meta]) => {
          if (!meta?.storage_path) return;
          try { urls[key] = await fetchViewUrl(meta.storage_path); } catch (_) { /* ignore */ }
        })
      );
      return urls;
    };
    (async () => {
      const [photos, videos] = await Promise.all([
        hydrate(deal.photos), hydrate(deal.videos),
      ]);
      if (!cancelled) setMediaUrls({ photos, videos });
    })();
    return () => { cancelled = true; };
  }, [deal, open]);

  // Fetch the calling investor's current interest record
  useEffect(() => {
    if (!deal || !open) return;
    let cancelled = false;
    (async () => {
      setLoadingInterest(true);
      try {
        const { data } = await axios.get(
          `${API}/deals/${deal.deal_id}/my-interest`,
          { withCredentials: true, headers: getAuthHeaders() }
        );
        if (!cancelled) {
          setIsInterested(!!data.interested);
          setNote(data.note || "");
        }
      } catch (_) {
        // default to not interested
      } finally {
        if (!cancelled) setLoadingInterest(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deal, open]);

  const saveInterest = async (interestedFlag) => {
    if (!deal) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/deals/${deal.deal_id}/interest`,
        { interested: interestedFlag, note: note || null },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      setIsInterested(interestedFlag);
      toast.success(
        interestedFlag
          ? "We let the admin know you're interested!"
          : "Interest removed"
      );
      onInterestChange?.(interestedFlag);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update interest");
    } finally {
       
      setSaving(false);
      onOpenChange(false)
    }
  };

  if (!deal) return null;

  const photoEntries = Object.entries(deal.photos || {}).filter(([, m]) => m?.storage_path);
  const videoEntries = Object.entries(deal.videos || {}).filter(([, m]) => m?.storage_path);
  const hasScope = deal.repair_notes || deal.additional_notes;
  const hasMedia = photoEntries.length > 0 || videoEntries.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 flex flex-col max-h-[90vh]"
        data-testid="investor-interest-modal"
      >
        {/* Sticky header */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
       
            Express Interest
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Review the deal and let the admin know you&apos;re interested. No NDA needed at this stage.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body — use native overflow so we don't fight the dialog flex */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {/* ── Step 2: Financials ── */}
          <section>
            <SectionHeader icon={DollarSign}>Financials</SectionHeader>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Asking Price" value={formatCurrency(deal.asking_price)} />
              <Stat label="Estimated Rehab" value={formatCurrency(deal.estimated_rehab_cost)} />
              <Stat label="Estimated ARV" value={formatCurrency(deal.estimated_arv)} />
              <Stat
                label="Closing Costs"
                value={
                  deal.closing_cost_and_commissions != null
                    ? `${deal.closing_cost_and_commissions}%`
                    : "—"
                }
              />
              {deal.wholesale_fee != null && (
                <Stat label="Wholesale Fee" value={formatCurrency(deal.wholesale_fee)} />
              )}
            </div>
          </section>

          {/* ── Step 3: Type ── */}
          {(deal.property_type || deal.deal_type) && (
            <>
              <Separator />
              <section>
                <SectionHeader icon={Home}>Type</SectionHeader>
                <div className="flex flex-wrap gap-2">
                  {deal.property_type && (
                    <Badge variant="secondary">{labelize(deal.property_type)}</Badge>
                  )}
                  {deal.deal_type && (
                    <Badge variant="secondary">{labelize(deal.deal_type)}</Badge>
                  )}
                </div>
              </section>
            </>
          )}

          {/* ── Step 4: Scope ── */}
          {hasScope && (
            <>
              <Separator />
              <section>
                <SectionHeader icon={FileText}>Scope of Work</SectionHeader>
                {deal.repair_notes && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">Repair Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{deal.repair_notes}</p>
                  </div>
                )}
                {deal.additional_notes && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Additional Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{deal.additional_notes}</p>
                  </div>
                )}
              </section>
            </>
          )}

          {/* ── Step 5: Media ── */}
          {hasMedia && (
            <>
              <Separator />
              <section data-testid="interest-media-section">
                <SectionHeader icon={Camera}>Media</SectionHeader>

                {photoEntries.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {photoEntries.map(([key, meta]) => {
                      const url = mediaUrls.photos[key];
                      return (
                        <a
                          key={key}
                          href={url || "#"}
                          target={url ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          onClick={(e) => { if (!url) e.preventDefault(); }}
                          className="group block aspect-square rounded-md border border-border overflow-hidden bg-muted hover:ring-2 hover:ring-blue-500/40"
                          title={meta.filename || labelize(key)}
                          data-testid={`interest-photo-${key}`}
                        >
                          {url ? (
                            <img
                              src={url}
                              alt={key}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Camera className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}
                        </a>
                      );
                    })}
                  </div>
                )}

                {videoEntries.length > 0 && (
                  <div className="space-y-1">
                    {videoEntries.map(([key, meta]) => {
                      const url = mediaUrls.videos[key];
                      return (
                        <a
                          key={key}
                          href={url || "#"}
                          target={url ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          onClick={(e) => { if (!url) e.preventDefault(); }}
                          className={`flex items-center justify-between gap-2 p-2 rounded-md border border-border bg-muted/30 ${url ? "hover:bg-muted" : "opacity-70 cursor-not-allowed"}`}
                          data-testid={`interest-video-${key}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Film className="w-4 h-4 text-blue-500 shrink-0" />
                            <span className="text-sm truncate">{meta.filename || labelize(key)}</span>
                          </div>
                          {url && <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                        </a>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}

          {/* ── Agreement & Consent (vacancy) ── */}
          <Separator />
          <section>
            <SectionHeader icon={Shield}>Agreement &amp; Consent</SectionHeader>
            <div className="space-y-2 text-sm">
              {deal.is_vacant === true && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span>Property is currently <span className="font-medium">vacant</span>.</span>
                </div>
              )}
              {deal.is_vacant === false && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-amber-700">Property is currently occupied</p>
                      {deal.occupancy_description && (
                        <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                          {deal.occupancy_description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {deal.is_vacant == null && (
                <p className="text-xs text-muted-foreground italic">Occupancy status not provided.</p>
              )}
            </div>
          </section>
        </div>

        {/* Sticky interest CTA — always visible */}
        <div
          className="border-t border-border bg-background px-6 py-4 space-y-3 shrink-0"
          data-testid="interest-cta-section"
        >
          <Textarea
            placeholder="Optional:  why this deal works for you (closing window, financing, contractor lined up)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            data-testid="interest-note-input"
          />

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="interest-close-btn"
              className="sm:w-auto"
            >
              Close
            </Button>

            {isInterested ? (
              <div className="flex flex-col-reverse sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => saveInterest(false)}
                  disabled={saving || loadingInterest}
                  data-testid="interest-remove-btn"
                >
                  Remove interest
                </Button>
                <Button
                  onClick={() => saveInterest(true)}
                  disabled={saving || loadingInterest}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="interest-save-btn"
                >
                  Update interest
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => saveInterest(true)}
                disabled={saving || loadingInterest}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="interest-express-btn"
              >
              
                I&apos;m Interested
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvestorInterestModal;
